import React, { useState, useEffect } from 'react';
import { Modal, Button, Badge, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import { X, CheckCircle, XCircle } from 'lucide-react';
import { applicationService } from '../services/applicationService';
import './Email.css';

const ApplicationView = ({ show, onHide, applicationId, onStatusChange }) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (show && applicationId) {
      loadApplication();
    } else {
      setApplication(null);
      setError(null);
    }
  }, [show, applicationId]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationService.getApplicationById(applicationId);
      if (response.success && response.data) {
        setApplication(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Are you sure you want to ${newStatus} this application?`)) {
      return;
    }

    try {
      setUpdating(true);
      await applicationService.updateApplicationStatus(applicationId, newStatus);
      // Close the modal and refresh the list
      if (onStatusChange) {
        onStatusChange();
      }
      onHide(); // Close the modal
    } catch (err) {
      alert(`Failed to update status: ${err.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'N/A';
    }
  };

  const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
  };

  const parseArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      if (value.includes(',')) {
        return value.split(',').map(v => v.trim());
      }
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Application Details
          {application && (
            <Badge bg={application.status === 'pending' ? 'warning' : application.status === 'accepted' ? 'success' : 'danger'} className="ms-2">
              {application.status.toUpperCase()}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : application ? (
          <div>
            {/* Section 1: Basic Personal Information */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Basic Personal Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Full Legal Name:</strong> {application.fullLegalName}</p>
                    {application.preferredName && (
                      <p><strong>Preferred Name:</strong> {application.preferredName}</p>
                    )}
                    <p><strong>Phone Number:</strong> {application.phoneNumber}</p>
                    <p><strong>Email Address:</strong> {application.emailAddress}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>City:</strong> {application.city}</p>
                    <p><strong>State:</strong> {application.state}</p>
                    <p><strong>ZIP Code:</strong> {application.zipCode}</p>
                    <p><strong>Authorized to Work in U.S.:</strong> {formatBoolean(application.authorizedToWork)}</p>
                    <p><strong>Requires Sponsorship:</strong> {formatBoolean(application.requiresSponsorship)}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 2: Position Information */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Position Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Position Applying For:</strong> {application.positionApplyingFor}</p>
                    <p><strong>Desired Start Date:</strong> {formatDate(application.desiredStartDate)}</p>
                    {application.desiredSalary && (
                      <p><strong>Desired Salary:</strong> {application.desiredSalary}</p>
                    )}
                  </Col>
                  <Col md={6}>
                    <p><strong>Employment Type:</strong> {parseArray(application.employmentType).join(', ')}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 3: Availability */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Availability</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>Days Available:</strong> {parseArray(application.daysAvailable).join(', ')}</p>
                    <p><strong>Hours Available:</strong> {application.hoursAvailable}</p>
                    <p><strong>Earliest Start Date:</strong> {formatDate(application.earliestStartDate)}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>Willing to Work Overtime:</strong> {formatBoolean(application.willingToWorkOvertime)}</p>
                    <p><strong>Willing to Work Weekends:</strong> {formatBoolean(application.willingToWorkWeekends)}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 4: Work Experience */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Work Experience</h5>
              </Card.Header>
              <Card.Body>
                {application.workExperience && Array.isArray(application.workExperience) && application.workExperience.length > 0 ? (
                  application.workExperience.map((exp, idx) => (
                    <div key={idx} className="mb-3 pb-3 border-bottom">
                      <h6>Experience #{idx + 1}</h6>
                      <p><strong>Company Name:</strong> {exp.companyName}</p>
                      <p><strong>Job Title:</strong> {exp.jobTitle}</p>
                      <p><strong>Location:</strong> {exp.location}</p>
                      <p><strong>Start Date:</strong> {formatDate(exp.startDate)}</p>
                      <p><strong>End Date:</strong> {formatDate(exp.endDate)}</p>
                      <p><strong>May We Contact This Employer:</strong> {formatBoolean(exp.mayWeContact)}</p>
                      <p><strong>Responsibilities:</strong> {exp.responsibilities}</p>
                      <p><strong>Reason for Leaving:</strong> {exp.reasonForLeaving}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No work experience provided.</p>
                )}
              </Card.Body>
            </Card>

            {/* Section 5: Education */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Education</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p><strong>School Name:</strong> {application.schoolName}</p>
                    <p><strong>Location:</strong> {application.schoolLocation}</p>
                    <p><strong>Degree/Certification:</strong> {application.degreeCertification}</p>
                  </Col>
                  <Col md={6}>
                    {application.fieldOfStudy && (
                      <p><strong>Field of Study:</strong> {application.fieldOfStudy}</p>
                    )}
                    <p><strong>Graduation Date:</strong> {formatDate(application.graduationDate)}</p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Section 6: Skills */}
            <Card className="mb-3">
              <Card.Header>
                <h5 className="mb-0">Skills</h5>
              </Card.Header>
              <Card.Body>
                <p><strong>Technical Skills:</strong></p>
                <p className="mb-3">{application.technicalSkills}</p>
                {application.softwareProficiency && (
                  <>
                    <p><strong>Software Proficiency:</strong></p>
                    <p className="mb-3">{application.softwareProficiency}</p>
                  </>
                )}
                {application.certificationsLicenses && (
                  <>
                    <p><strong>Certifications/Licenses:</strong></p>
                    <p className="mb-3">{application.certificationsLicenses}</p>
                  </>
                )}
                {application.languagesSpoken && (
                  <p><strong>Languages Spoken:</strong> {application.languagesSpoken}</p>
                )}
              </Card.Body>
            </Card>

            <div className="text-muted small">
              <p><strong>Submitted:</strong> {formatDate(application.createdAt)}</p>
            </div>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        {application && application.status === 'pending' && (
          <>
            <Button
              variant="success"
              onClick={() => handleStatusUpdate('accepted')}
              disabled={updating}
              title="Accept Application"
            >
              <CheckCircle size={18} />
            </Button>
            <Button
              variant="danger"
              onClick={() => handleStatusUpdate('rejected')}
              disabled={updating}
              title="Reject Application"
            >
              <XCircle size={18} />
            </Button>
          </>
        )}
        <Button variant="secondary" onClick={onHide} title="Close">
          <X size={18} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApplicationView;

